import { useCallback, useRef, useEffect } from 'react';
import { createLimiter } from '../utils/limiter';
// 0.7: at most MATTERGEN_MAX_PARALLEL MatterGen jobs are in flight across a whole run, every
// row included; the same export MattergenRunner uses for its per-call fallback.
import { MATTERGEN_MAX_PARALLEL } from '../components/mattergen/mattergenJobs';
import { sumGrants } from '../utils/runGrant';
import { PLAN_LIMITS } from '../config/planLimits';
import { rowEndpoint } from '../context/customModelSelection';
import { prepareRows, rowLabel, NOTHING_RUNNABLE_MESSAGE } from '../ai/rows/prepareRows';
import { mattergenPayloadForRow, fm4mPayloadForRow, gt4sdPayloadForRow } from '../ai/rows/rowPayloads';

// Part 1a (editable-plan spec, "Dispatch"; contract 1.5): rows are the only dispatch source.
// runRows sends exactly the rows it is given, each through rowEndpoint and its runner's
// per-row payload; nothing at dispatch routes, re-derives a param or picks a custom model.

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isAbortError = (err) => err?.name === 'AbortError';
const errorMessage = (err) => String((err && err.message) || err);

/**
 * The run labels of one run (contract 1.5 step 6): rowLabel(row), with ` (<row.id>)` appended
 * when another row of the run has the same rowLabel, so two bars or two rowStatus lines are
 * never named alike. Keyed by row id; a value that is not a row with a string id gets none.
 */
function runLabels(rows) {
  const named = (Array.isArray(rows) ? rows : []).filter((row) => isPlainObject(row) && typeof row.id === 'string' && row.id);
  const counts = new Map();
  named.forEach((row) => counts.set(rowLabel(row), (counts.get(rowLabel(row)) || 0) + 1));
  const labels = new Map();
  named.forEach((row) => {
    const label = rowLabel(row);
    labels.set(row.id, counts.get(label) > 1 ? `${label} (${row.id})` : label);
  });
  return labels;
}

/** Row ids in row order, as prepareRows reports them (`invalid-<position>` for a value that is not a row). */
function rowIdsInOrder(rows) {
  return (Array.isArray(rows) ? rows : []).map((row, i) => (isPlainObject(row) && typeof row.id === 'string' && row.id ? row.id : `invalid-${i + 1}`));
}

export default function useToolsRunner({
  mattergenRef,
  fm4mRef,
  gt4sdRef,
  abortController,
  setProgress,
  setToolResults,
  familyRouting,
  // C5: these MUST be passed as arguments. This hook runs inside AppProvider,
  // above the <AppContext.Provider> it renders, so useAppContext() here reads
  // the default (null) context and silently drops every value.
  // Org model policy (2026-08-22), same C5 reasoning: must be passed in.
  modelPolicy,
  // Plan limits (src/config/planLimits.js getOrgLimits().limits), same C5 reasoning.
  // maxCandidatesPerRun bounds the whole run for every tier (0.4); missing = pilot.
  planLimits,
}) {

  // Keep refs in sync so callbacks always see the latest values
  const acRef = useRef(abortController);
  useEffect(() => { acRef.current = abortController; }, [abortController]);
  const modelPolicyRef = useRef(modelPolicy);
  useEffect(() => { modelPolicyRef.current = modelPolicy; }, [modelPolicy]);
  const familyRoutingRef = useRef(familyRouting);
  useEffect(() => { familyRoutingRef.current = familyRouting; }, [familyRouting]);
  const planLimitsRef = useRef(planLimits);
  useEffect(() => { planLimitsRef.current = planLimits; }, [planLimits]);

  // The Execute-time context for prepareRows, read from the refs at call time (never a
  // render-time closure, contract 1.5).
  const ctxFromRefs = useCallback(() => ({
    customModels: familyRoutingRef.current?.customModels || [],
    modelPolicy: modelPolicyRef.current,
    planMax: Number(planLimitsRef.current?.maxCandidatesPerRun) || PLAN_LIMITS.pilot.maxCandidatesPerRun,
    byocActive: !!familyRoutingRef.current?.byocActive,
    byocEndpoints: familyRoutingRef.current?.byocEndpoints || {},
  }), []);

  /**
   * The Execute-time backstop (decision L1a): prepareRows over the current custom models,
   * policy, plan max and BYOC endpoints, ranked by `targets` (spec.targets). The caller writes
   * the result to executedRows and hands the same object to runRows as `prepared`.
   */
  const prepareForRun = useCallback((rows, targets) => prepareRows(rows, { ...ctxFromRefs(), targets }), [ctxFromRefs]);

  /**
   * Runs exactly the given rows (contract 1.5). Reads only row fields for dispatch; `spec` is
   * read only for spec.targets (backstop-fit priority) when it prepares the rows itself.
   * Returns the toolResults object.
   * opts.prepared: the prepareForRun(rows, spec.targets) result the caller already wrote to
   *   executedRows. When given, runRows skips its own prepare, dispatches prepared.rows as given
   *   and seeds rowStatus from prepared.rejected. Without it (unit tests), runRows calls
   *   prepareRows itself.
   */
  const runRows = useCallback(async (rows, spec, opts = {}) => {
    const { onStep, runId, prepared } = opts || {};
    const list = Array.isArray(rows) ? rows : [];
    const results = { rowStatus: {} };
    // D1: fires after the MatterGen block and after each FM4M or GT4SD row settles, i.e. at
    // every point `results` grows. AppProvider wires this to a Firestore patch so a browser
    // close mid-run leaves whatever landed so far durably readable elsewhere. A shallow copy:
    // rowStatus is replaced, never edited in place, so an earlier snapshot never changes.
    const notifyStep = () => {
      if (typeof onStep === 'function') {
        try { onStep({ ...results }); } catch (_) {}
      }
    };
    // H17 / 0.1: one run id rides every job submit this run fans out to (chunks x rows). The
    // caller passes the Firestore run doc id as opts.runId, so the daily-cap slot, the per-run
    // tally, the job docs' spectra_run_id and the run doc share ONE id. A fresh id is minted
    // only when the caller supplies none.
    const spectraRunId = (typeof runId === 'string' && runId.length > 0)
      ? runId
      : (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    const withRunId = (headers) => ({ ...(headers || {}), 'X-Spectra-Run-Id': spectraRunId });
    if (typeof setProgress === 'function') setProgress({});

    const p = prepared || prepareRows(list, { ...ctxFromRefs(), targets: spec?.targets });
    const labels = runLabels(list);
    const labelOf = (row) => labels.get(row.id) || rowLabel(row);
    // Progress is keyed by row id (step 6), so two rows on one checkpoint get two bars.
    const bar = (id, label, done, total, status) => {
      if (typeof setProgress === 'function') {
        setProgress((prev) => ({ ...prev, [id]: { key: id, done, total, label, status } }));
      }
    };
    const setRowStatus = (id, entry) => { results.rowStatus = { ...results.rowStatus, [id]: entry }; };

    // Step 3: every rejected row is recorded with its reason and a failed bar (D16, D17).
    for (const r of (Array.isArray(p.rejected) ? p.rejected : [])) {
      const label = labels.get(r.rowId) || String(r.rowId);
      const source = list.find((row) => isPlainObject(row) && row.id === r.rowId);
      const total = source ? Math.max(0, Math.floor(Number(source.candidates)) || 0) : 0;
      setRowStatus(r.rowId, { status: 'rejected', label, reason: r.reason, message: r.message });
      bar(r.rowId, label, 0, total, 'error');
    }

    const accepted = Array.isArray(p.rows) ? p.rows : [];
    if (p.scaled) {
      try { console.info && console.info(`[RUN TOTAL] rows scaled to fit the plan max of ${ctxFromRefs().planMax} candidates`); } catch (_) {}
    }
    try { console.debug && console.debug('[ROWS]', accepted.map((r) => `${r.id} ${r.service}:${r.target}(${r.candidates})`)); } catch (_) {}
    const routing = familyRoutingRef.current || {};
    const mgRows = accepted.filter((r) => r.service === 'mattergen');
    const fmRows = accepted.filter((r) => r.service === 'fm4m');
    const gtRows = accepted.filter((r) => r.service === 'gt4sd');

    // MatterGen rows launch together; one limiter per run (0.7) keeps at most
    // MATTERGEN_MAX_PARALLEL jobs in flight across all of them.
    if (mgRows.length > 0 && mattergenRef.current?.execute) {
      const mattergenLimiter = createLimiter(MATTERGEN_MAX_PARALLEL);
      const rowPromises = mgRows.map((row) => {
        const label = labelOf(row);
        const total = row.candidates;
        const { baseUrl, authHeaders } = rowEndpoint(row, routing);
        bar(row.id, label, 0, total, 'running');
        const onRowProgress = ({ done }) => bar(row.id, label, done, total, 'running');
        return mattergenRef.current.execute(mattergenPayloadForRow(row), { onProgress: onRowProgress, limiter: mattergenLimiter, signal: acRef.current?.signal, baseUrl, authHeaders: withRunId(authHeaders) })
          .then((res) => {
            // 0.7: a row that kept only some jobs ends 'partial', its bar showing the
            // candidates that actually landed; its failed jobs are tagged with the row.
            const failed = Array.isArray(res?.failed_jobs) ? res.failed_jobs : [];
            // 0.3: when the runs report what the plan granted, the bar ends at that count.
            const granted = sumGrants(res?.runs).granted;
            if (failed.length === 0) {
              bar(row.id, label, granted ?? total, total, 'done');
              setRowStatus(row.id, { status: 'done', label });
              return res;
            }
            const lost = failed.reduce((sum, f) => sum + (Number(f?.candidates) || 0), 0);
            bar(row.id, label, granted ?? Math.max(0, total - lost), total, 'partial');
            setRowStatus(row.id, { status: 'partial', label });
            return { ...res, failed_jobs: failed.map((f) => ({ ...f, rowId: row.id, variant: row.target })) };
          })
          .catch((err) => {
            bar(row.id, label, 0, total, 'error');
            setRowStatus(row.id, { status: 'error', label, message: errorMessage(err) });
            throw err;
          });
      });

      // allSettled so one failed row doesn't discard the others' successful results.
      const settled = await Promise.allSettled(rowPromises);
      // Terminal rule (a), B5: a cancel can no longer finish 'complete'.
      if (acRef.current?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const fulfilled = settled.filter((s) => s.status === 'fulfilled').map((s) => s.value);
      const failures = settled.filter((s) => s.status === 'rejected').map((s) => s.reason);
      // Terminal rule (a): every MatterGen row failed, so the run fails before any FM4M or
      // GT4SD row and before notifyStep, as today. Nothing is attached to this error, so a
      // failed crystal run still makes exactly 2 writes (start and finish(error)).
      if (fulfilled.length === 0) {
        throw failures.find((e) => !isAbortError(e)) || failures[0] || new Error('All MatterGen rows failed');
      }
      if (failures.length > 0) {
        console.warn(`MatterGen: ${failures.length}/${mgRows.length} row(s) failed; continuing with ${fulfilled.length} succeeded.`, failures);
      }
      for (const res of fulfilled) {
        if (!results.mattergen) results.mattergen = res;
        else results.mattergen = { ok: true, total_requested: (Number(results.mattergen.total_requested) || 0) + (Number(res.total_requested) || 0), runs: [...(results.mattergen.runs || []), ...(res.runs || [])], failed_jobs: [...(results.mattergen.failed_jobs || []), ...(res.failed_jobs || [])] };
      }
      notifyStep();
    }

    // FM4M rows, one after another. Terminal rule (b): a failed row is isolated (its row
    // ends 'error' and the loop continues); an AbortError (user cancel) still propagates.
    for (const row of fmRows) {
      if (!fm4mRef.current?.execute) continue;
      const label = labelOf(row);
      const total = row.candidates;
      const { baseUrl, authHeaders } = rowEndpoint(row, routing);
      bar(row.id, label, 0, total, 'running');
      const onRowProgress = ({ done }) => bar(row.id, label, done, total, 'running');
      try {
        const res = await fm4mRef.current.execute(fm4mPayloadForRow(row), { onProgress: onRowProgress, signal: acRef.current?.signal, baseUrl, authHeaders: withRunId(authHeaders) });
        bar(row.id, label, total, total, 'done');
        // Terminal rule (d): "the generator returned no usable SMILES" counts as done.
        setRowStatus(row.id, res?.skipped
          ? { status: 'done', label, reason: 'no_output', ...(typeof res.reason === 'string' ? { message: res.reason } : {}) }
          : { status: 'done', label });
        if (!results.fm4m) results.fm4m = res;
        else results.fm4m = { ok: true, total_requested: (Number(results.fm4m.total_requested) || 0) + (Number(res.total_requested) || 0), runs: [...(results.fm4m.runs || []), ...(res.runs || [])] };
      } catch (err) {
        bar(row.id, label, 0, total, 'error');
        if (isAbortError(err)) throw err;
        setRowStatus(row.id, { status: 'error', label, message: errorMessage(err) });
        console.warn(`FM4M: row ${row.id} failed; continuing with other rows.`, err);
      }
      // D1/D3 mitigation: persist the moment this row's response lands, per row rather than
      // only at the end of the run, so a tab close loses at most one in-flight request.
      notifyStep();
    }

    // GT4SD rows, one after another, with the same isolation as the FM4M loop.
    for (const row of gtRows) {
      if (!gt4sdRef?.current?.execute) continue;
      const label = labelOf(row);
      const total = row.candidates;
      const { baseUrl, authHeaders } = rowEndpoint(row, routing);
      bar(row.id, label, 0, total, 'running');
      const onRowProgress = ({ done }) => bar(row.id, label, done, total, 'running');
      try {
        const res = await gt4sdRef.current.execute(gt4sdPayloadForRow(row), { onProgress: onRowProgress, signal: acRef.current?.signal, baseUrl, authHeaders: withRunId(authHeaders) });
        bar(row.id, label, total, total, 'done');
        setRowStatus(row.id, { status: 'done', label });
        if (!results.gt4sd) results.gt4sd = res;
        else results.gt4sd = { ok: true, total_requested: (Number(results.gt4sd.total_requested) || 0) + (Number(res.total_requested) || 0), runs: [...(results.gt4sd.runs || []), ...(res.runs || [])] };
      } catch (err) {
        bar(row.id, label, 0, total, 'error');
        if (isAbortError(err)) throw err;
        setRowStatus(row.id, { status: 'error', label, message: errorMessage(err) });
        console.warn(`GT4SD: row ${row.id} failed; continuing with other rows.`, err);
      }
      // D1/D3 mitigation: gt4sd-service has no job concept beyond this request; persist
      // the moment it lands.
      notifyStep();
    }

    // Terminal rule (c), after the last row.
    if (acRef.current?.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const statuses = results.rowStatus;
    const finished = Object.values(statuses).some((s) => s.status === 'done' || s.status === 'partial');
    if (!finished) {
      const order = [...rowIdsInOrder(list), ...Object.keys(statuses)];
      const firstFailed = order.find((id) => statuses[id] && (statuses[id].status === 'error' || statuses[id].status === 'rejected'));
      const err = new Error((firstFailed && statuses[firstFailed].message) || NOTHING_RUNNABLE_MESSAGE);
      err.toolResults = results;
      throw err;
    }

    if (typeof setToolResults === 'function') setToolResults(results);
    return results;
  }, [mattergenRef, fm4mRef, gt4sdRef, setProgress, setToolResults, ctxFromRefs]);

  const cancelAll = useCallback(() => {
    // 1. Abort all pending fetch requests (client-side)
    try { acRef.current && acRef.current.abort(); } catch (_) {}
    // 2. Kill MatterGen GPU subprocesses on the backend (server-side)
    try { mattergenRef.current?.cancelAll?.(); } catch (_) {}
  }, [mattergenRef]);

  // #36: expose acRef so callers can sync it immediately before a run
  return { runRows, prepareForRun, cancelAll, acRef };
}
